import { apiClient } from './client';

/** `unknown` = the customer has no Odoo counterpart, so nothing can be said. */
export type PaymentStatus = 'paid' | 'outstanding' | 'overdue' | 'not_invoiced' | 'unknown';
export type BlockedOn =
  | 'odoo_link'
  | 'subscription'
  | 'invoice'
  | 'send'
  | 'payment'
  | null;
export type OdooLinkSource = 'odoo_partner_id' | 'manual' | 'name';
export type SubscriptionPhase = 'quotation' | 'scheduled' | 'live' | 'ended';
/** active = contract running or starting later; onboarding = won, no contract yet; former = all ended. */
export type CustomerStage = 'active' | 'onboarding' | 'former' | 'unknown';

export type InvoiceInsightKind =
  | 'payment_sent' // customer says the payment was made / is on its way
  | 'will_pay_on' // customer commits to a date or period ("end of October")
  | 'redirect_recipient' // customer asks to send the invoice/reminders to someone else
  | 'dispute' // customer contests the amount, the contract, or refuses
  | 'question' // customer asks something that needs a reply before payment
  | 'none'; // nothing actionable in the thread (or no inbound mail at all)

/** What the LATEST relevant customer (inbound) mail on the invoice thread says. One per invoice, newest wins. */
export interface InvoiceThreadInsight {
  kind: InvoiceInsightKind;
  /** ISO date the customer claims the payment was made / will be made. Null when not stated. */
  claimedDate: string | null;
  /** New recipients the customer asked for (redirect_recipient), else []. */
  recipients: string[];
  /** One sentence in plain words, e.g. "Holly wrote on 25 Sep that the wire went out on 24 Sep." */
  summary: string;
  /** Verbatim excerpt (≤ 200 chars) from the mail that supports the classification. */
  evidence: string;
  confidence: 'high' | 'medium' | 'low';
  /** The inbound mail this was read from. */
  mailAt: string;
  mailFrom: string | null;
  /** When the OS read it. */
  analysedAt: string;
  /** What the OS does with it, in plain words: "Customer says paid 24 Sep · reminders paused · check on 8 Oct". */
  effect: string;
  /** ISO date the OS re-raises this if still unpaid. Null otherwise. */
  followUpOn: string | null;
  /** redirect_recipient: when the thread last carried a mail to the requested recipient. Set = done. */
  recipientsMailedAt?: string | null;
}

/** An MRR-sheet override that disagrees with the invoice for the same term by more than 10%. */
export interface OverrideInvoiceConflict {
  termMonths: number;
  overrideTotal: number;
  invoicedTotal: number;
  documentCurrency: string;
  overrideInDocument: number;
  invoicedInDocument: number;
  invoiceNumbers: string[];
  gapShare: number;
}

export interface CustomerInvoice {
  id: string;
  odooInvoiceId: number;
  invoiceNumber: string;
  status: string;
  paymentState: string | null;
  /** Odoo's own sent flag (or paid). NULL = not synced yet: unknown, not unsent. */
  sent: boolean | null;
  issueDate: string;
  dueDate: string;
  paidDate: string | null;
  /** Signed, in `currency` (accounting): a credit note is negative. */
  total: number;
  /** Still open. NULL = not synced, which is unknown rather than settled. */
  residual: number | null;
  currency: string;
  /** The currency the document was issued in — USD for One Pass, ISS North America. */
  documentCurrency: string;
  /** Gross total in `documentCurrency`, unsigned. */
  documentTotal: number;
  overdueDays: number | null;
  /** An Odoo credit note: listed, never counted as an invoice. */
  isCreditNote: boolean;
  /** Dated after today: booked in Odoo, not owed yet, and not in `outstanding`. */
  scheduled: boolean;
  /** Most urgent payment reminder drafted for this invoice, and the day. `at` null = day not recorded. */
  reminder: { tier: string; at: string | null } | null;
  /** What the customer's latest mail on the thread says. Null = no thread read. */
  insight: InvoiceThreadInsight | null;
  /**
   * What settled it: paid by bank, cleared by a credit note, a credit note set
   * against an invoice, or a credit note refunded. Optional: older API builds lack it.
   */
  settledBy?: 'payment' | 'refund' | 'credit_note' | 'invoice' | 'netted' | null;
  /** The other document for a credit-note settlement. */
  settledAgainst?: string | null;
  /** The one bank entry that paid or refunded it ("BNK1/2026/00082"). Optional. */
  settledRef?: string | null;
  /** Out of the reminder ladder on purpose: never chased. */
  muted?: { reason: string } | null;
}

/** What the chase digest last raised for the customer. */
export interface CustomerLastChased {
  kind: string;
  at: string;
}

/** One Odoo partner behind a customer. Most have one; Xerox has two. */
export interface CustomerOdooPartner {
  companyId: string;
  odooPartnerId: number | null;
  name: string;
  linkedBy: OdooLinkSource;
  /** Set when the link was made by hand — what "unlink" removes. */
  linkId: string | null;
}

export interface CustomerDeal {
  id: string;
  attioDealRecordId: string;
  attioUrl: string;
  dealName: string | null;
  closedWonAt: string;
  dealValue: number | null;
  currency: string;
  orderFormUrl: string | null;
}

export interface UninvoicedOrder {
  subscriptionId: string;
  orderName: string;
  /** ISO day Odoo was due to invoice the order. */
  nextInvoiceOn: string;
  daysLate: number;
  sourceMrr: number | null;
  sourceCurrency: string | null;
  mrrEur: number | null;
  /** One billing period of the order in `sourceCurrency` (S00210, yearly: 45,000). Null = not synced. */
  invoiceAmount?: number | null;
}

export interface CustomerRow {
  id: string;
  companyName: string;
  companyId: string | null;
  odooCompanyId: string | null;
  odooPartnerId: number | null;
  linkedBy: OdooLinkSource | null;
  odooPartners: CustomerOdooPartner[];
  attioDealRecordId: string;
  attioUrl: string;
  dealName: string | null;
  ownerName: string | null;
  closedWonAt: string;
  latestWonAt: string;
  deals: CustomerDeal[];
  dealValue: number | null;
  currency: string;

  orderFormUrl: string | null;
  orderFormSignedAt: string | null;
  notes: string | null;

  subscription: {
    planName: string | null;
    status: string | null;
    phase: SubscriptionPhase | null;
    odooState: string | null;
    /** EUR. NULL = not converted; show it as unknown, never as zero. */
    mrr: number | null;
    sourceMrr: number | null;
    sourceCurrency: string | null;
    liveCount: number;
    startsOn: string | null;
    expiresOn: string | null;
    nextInvoiceDate: string | null;
    /** This month's MRR for the customer, as the Overview counts it (overrides, setup fees). Null = not linked. */
    reportedMrr: number | null;
    /** Set when a reporting override replaces the Odoo amount this month. */
    mrrOverride: { note: string; contractMrr: number; invoiceConflict?: OverrideInvoiceConflict | null } | null;
  };
  invoicing: {
    created: number;
    sent: number;
    /** Paid by payment; newer API builds leave credited invoices out (see `credited`). */
    paid: number;
    /** Invoices cleared by a credit note. Optional: older API builds lack it. */
    credited?: number;
    /** Cancelled and never-posted documents on the linked partners. Optional. */
    unposted?: { cancelled: number; draft: number };
    /** Real invoices dated today or earlier, gross: credit notes are in `totalCredited`. */
    totalInvoiced: number;
    /** Credit notes that reverse one of these invoices, positive; net invoiced = totalInvoiced − this. Optional: older API builds lack it. */
    totalCredited?: number;
    /** Settled invoices, gross: an invoice settled by a credit note counts here too. */
    totalPaid: number;
    outstanding: number;
    outstandingIncomplete: boolean;
    /** Open on invoices dated after today; not part of `outstanding`. */
    scheduled: number;
    oldestOverdueDays: number | null;
    invoices: CustomerInvoice[];
  };

  /**
   * Live Odoo orders past their next invoice date with nothing invoiced
   * (3+ days late). Odoo moves the date on when it invoices, so a date left
   * behind is a billing that never happened. Empty is the normal case.
   */
  uninvoicedOrders: UninvoicedOrder[];
  steps: {
    orderForm: boolean;
    subscription: boolean;
    invoiceCreated: boolean;
    invoiceSent: boolean;
    paid: boolean;
  };
  paymentStatus: PaymentStatus;
  blockedOn: BlockedOn;
  stage: CustomerStage;
  lastChased: CustomerLastChased | null;
  /** What to do next, computed by the API so tab and DM say the same thing. Null = nothing. */
  nextStep: string | null;
}

export interface CustomerList {
  customers: CustomerRow[];
  summary: {
    total: number;
    byStage: Record<CustomerStage, number>;
    paid: number;
    outstanding: number;
    overdue: number;
    notInvoiced: number;
    unknown: number;
    outstandingAmount: number;
    overdueAmount: number;
    /** True when an invoice has no residual synced, so the amounts are a floor. */
    outstandingIncomplete: boolean;
    /** Booked on invoices dated after today; not part of `outstandingAmount`. */
    scheduledAmount: number;
    liveMrr: number;
    liveMrrIncomplete: boolean;
    /** This month's MRR, the Overview's number, split into Active customers and the rest. */
    mrr:
      | {
          month: string;
          total: number;
          activeTotal: number;
          activeCustomers: number;
          /** The register row it is on (a contract that ended this month); null = no row. */
          others: { customerId: string | null; name: string; mrr: number; why: string }[];
          error: null;
        }
      | { error: string };
    currency: string;
  };
  linkage: {
    linked: number;
    unlinked: number;
    byPartnerId: number;
    byManual: number;
    byName: number;
    unlinkedCustomers: string[];
  };
  reconciled: { created: number; linked: number };
}

export async function getCustomers(): Promise<CustomerList> {
  return apiClient.get('/customers');
}

export async function updateCustomer(
  id: string,
  input: {
    orderFormUrl?: string | null;
    orderFormSignedAt?: string | null;
    notes?: string | null;
    archived?: boolean;
  },
): Promise<{ success: boolean }> {
  return apiClient.patch(`/customers/${id}`, input);
}

export interface OdooPartnerOption {
  companyId: string;
  odooPartnerId: number;
  name: string;
}

export async function searchOdooPartners(query: string): Promise<{ partners: OdooPartnerOption[] }> {
  return apiClient.get(`/customers/odoo-partners?q=${encodeURIComponent(query)}`);
}

export async function addCustomerOdooLink(input: {
  crmName: string;
  odooPartnerId: number;
  note?: string | null;
}): Promise<{ id: string; partnerName: string }> {
  return apiClient.post('/customers/odoo-links', input);
}

export async function removeCustomerOdooLink(id: string): Promise<{ success: boolean }> {
  return apiClient.delete(`/customers/odoo-links/${id}`);
}

// ─── Customer detail (GET /customers/:id/detail) ────────────────────────────
// Mirrors the API contract exactly; `:id` is CustomerRow.id.

export interface CustomerDetail {
  /** The same row the list shows, so header numbers never disagree. */
  customer: CustomerRow;
  subscriptions: CustomerSubscription[];
  /** Newest first; credit notes included with isCreditNote. */
  invoices: CustomerDetailInvoice[];
  /** Oldest first. */
  timeline: TimelineEvent[];
  /** Non-null when the live Odoo read (mail threads) failed; everything else still returned. Never hide it. */
  odooError: string | null;
  /** MRR by month as the Overview counts it, beside Odoo's contracts alone. Oldest first. */
  mrrHistory: CustomerMrrMonth[];
  /** Non-null when the MRR engine could not be read. Never hide it. */
  mrrError: string | null;
}

export interface CustomerMrrMonth {
  month: string;
  /** What the Overview counts for this customer in the month. */
  mrr: number;
  /** Odoo's contracts alone, before any reporting override. */
  odooMrr: number;
  /** The reporting override in effect, from the MRR sheet. */
  override: { note: string; mrr: number; from: string; to: string | null } | null;
  /** The first month after an override ended: the contract rate is back. */
  restored: boolean;
}

export type SubscriptionDetailPhase = 'live' | 'scheduled' | 'ended' | 'quotation' | 'cancelled' | 'unknown';

export interface CustomerSubscription {
  id: string;
  odooOrderName: string;
  planName: string | null;
  /** Odoo subscription_state as-is ("3_progress", "6_churn", …). */
  odooState: string | null;
  phase: SubscriptionDetailPhase;
  startsOn: string | null;
  endsOn: string | null;
  nextInvoiceOn: string | null;
  /** Live order past its Odoo next invoice date with nothing invoiced: that date. */
  invoiceDueSince: string | null;
  /** EUR. */
  mrr: number | null;
  sourceMrr: number | null;
  sourceCurrency: string;
  odooUrl: string | null;
  /** The order that renews this one, else null. Optional: older API builds lack it. */
  renewedBy?: string | null;
}

export interface InvoiceMail {
  at: string;
  direction: 'out' | 'in';
  subject: string | null;
  /** Recipient emails/names as Odoo gives them (empty for inbound). */
  to: string[];
  author: string | null;
  /** First ~300 chars of the plain-text body. */
  excerpt: string;
}

export interface ReminderHistory {
  at: string;
  tier: string;
  /** True when the automation found a mail already sent for that tier and skipped the draft. */
  skippedAsHandled: boolean;
}

export interface ReminderDraft {
  tier: string;
  /**
   * reached = due now; next = nothing due today, next tier in the ladder;
   * overdue_gap = past due under a week, offered for manual use;
   * insight = built from what the customer said (payment claim to check, promise missed).
   */
  basis: 'reached' | 'next' | 'overdue_gap' | 'insight';
  firesOn: string | null;
  to: string[];
  subject: string;
  /** Plain text, ready to paste into the Odoo "Send" dialog. */
  body: string;
  /** What the draft was built from, in plain words. */
  basedOn: string;
  greetingGuessed: boolean;
}

export interface CustomerDetailInvoice extends CustomerInvoice {
  odooUrl: string | null;
  /** Payment terms in days = due − issue. */
  termDays: number;
  /** Odoo mail thread on the invoice, oldest first. */
  mails: InvoiceMail[];
  /** Every reminder draft the automation ever produced for this invoice, oldest first. */
  reminders: ReminderHistory[];
  muted: { reason: string } | null;
  /** Present for open, unpaid, non-credit, non-future-dated invoices. */
  draft: ReminderDraft | null;
}

export type TimelineKind =
  | 'deal_won'
  | 'subscription_started'
  | 'subscription_ends'
  | 'subscription_ended'
  | 'invoice_issued'
  | 'invoice_sent'
  | 'invoice_due'
  | 'invoice_paid'
  | 'credit_note'
  | 'mail_out'
  | 'mail_in'
  | 'reminder_drafted'
  | 'chase_raised';

export interface TimelineEvent {
  at: string;
  kind: TimelineKind;
  title: string;
  detail: string | null;
  /** True for dates after today, so the UI can grey them. */
  future: boolean;
  url: string | null;
  invoiceNumber: string | null;
}

/** Null when the API answers 404 — the id is not a customer. Any other failure throws. */
export async function getCustomerDetail(id: string): Promise<CustomerDetail | null> {
  const data = await apiClient.get<CustomerDetail | { error?: unknown }>(
    `/customers/${encodeURIComponent(id)}/detail`,
    { passthroughStatuses: [404] },
  );
  return 'customer' in data && data.customer ? data : null;
}
